import type { CallRequest } from "@/interfaces/api";
import { APIError } from "@/utils/error";
import { api } from ".";

const formatBody = (body: any) => {
  if (Array.isArray(body)) {
    return { data: body };
  }

  return body;
};

async function clientCall<T>({ path, returnStatusCode, ...rest }: CallRequest): Promise<T> {
  try {
    const response = await api.call(path, rest);
    const body = await api.parse<T>(response);

    if (api.hasError(response, body)) {
      if (response.status === 401) {
        api.handleUnauthorizedError();
      }

      return {
        error: {
          ...formatBody(body),
          statusCode: returnStatusCode ? response.status : undefined,
        },
      } as T;
    }

    const formattedBody = formatBody(body);

    if (formattedBody && returnStatusCode) {
      return {
        ...formattedBody,
        statusCode: response.status,
      };
    }

    return formattedBody;
  } catch (error: any) {
    // TODO: integrar Sentry

    if (error instanceof APIError) {
      throw error;
    }

    console.error("Ocorreu um erro inesperado:", error);
    throw new APIError({ message: api.defaultError }, 500);
  }
}

/**
 *  @note Retorna um objeto { data: T } se a resposta da requisição (T) for um array
 */
export async function clientGet<T>(payload: Omit<CallRequest, "method" | "data">): Promise<T> {
  return clientCall({ method: "GET", ...payload });
}

export async function clientPost<T>(payload: Omit<CallRequest, "method">): Promise<T> {
  return clientCall({ method: "POST", ...payload });
}

export async function clientPatch<T>(payload: Omit<CallRequest, "method">): Promise<T> {
  return clientCall({ method: "PATCH", ...payload });
}

export async function clientPut<T>(payload: Omit<CallRequest, "method">): Promise<T> {
  return clientCall({ method: "PUT", ...payload });
}

export async function clientDelete<T>(payload: Omit<CallRequest, "method">): Promise<T> {
  return clientCall({ method: "DELETE", ...payload });
}
